import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosClient from '../api/axiosClient';

// --------------------------------------------------
// Types based on REAL BACKEND API
// --------------------------------------------------
export interface UserType {
  id: number;
  name: string;
  email: string;
  company_id: number | null;
  department_id: number | null;
  is_active: boolean;
  role_id: number;
  created_at: string;
  role_name: string;
  company_name: string;
  department_name: string;
}

// --------------------------------------------------
// Payload types for API calls
// --------------------------------------------------
export interface CreateUserPayload {
  name: string;
  email: string;
  company_id: number;
  department_id: number;
  password: string;
  role_id: number;
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  department_id?: number;
  is_active?: boolean;
}

// --------------------------------------------------
// State
// --------------------------------------------------
interface UserState {
  data: UserType[];
  loading: boolean;
  error: string | null;
  currentUser: UserType | null; // ← ADDED: Current user state
}

const initialState: UserState = {
  data: [],
  loading: false,
  error: null,
  currentUser: null // ← ADDED: Initial current user state
};

// --------------------------------------------------
// Helper: extract backend error safely
// --------------------------------------------------
function extractError(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    // Axios error structure
    if ('response' in err) {
      const axiosErr = err as {
        response?: {
          data?: {
            detail?: string | Array<{ msg: string }>;
          };
        };
      };

      const detail = axiosErr.response?.data?.detail;

      // detail is a string
      if (typeof detail === 'string') {
        return detail;
      }

      // detail is an array of objects
      if (Array.isArray(detail)) {
        const first = detail[0];

        if (first && typeof first === 'object' && 'msg' in first) {
          return first.msg;
        }

        return 'Validation error';
      }

      // unknown detail format
      return 'Unknown server error';
    }

    // Generic JS error object
    if (
      'message' in err &&
      typeof (err as { message: unknown }).message === 'string'
    ) {
      return (err as { message: string }).message;
    }
  }

  return 'Unexpected error occurred';
}

// --------------------------------------------------
// Fetch Users - GET /users/ with pagination and filters
// --------------------------------------------------
export const fetchUsers = createAsyncThunk<
  UserType[],
  {
    skip?: number;
    limit?: number;
    company_id?: number;
    department_id?: number;
    role_id?: number;
  } | void, // ← ADDED: department_id and role_id
  { rejectValue: string }
>('users/fetchUsers', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosClient.get('/users/', {
      params: {
        skip: params?.skip || 0,
        limit: params?.limit || 100,
        company_id: params?.company_id,
        department_id: params?.department_id, // ← ADDED: department filter
        role_id: params?.role_id // ← ADDED: role filter
      }
    });
    return res.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// --------------------------------------------------
// Create User - POST /users/
// --------------------------------------------------
export const addUser = createAsyncThunk<
  UserType,
  CreateUserPayload,
  { rejectValue: string }
>('users/addUser', async (newUser, { rejectWithValue }) => {
  try {
    const res = await axiosClient.post('/users/', newUser);
    return res.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// --------------------------------------------------
// Edit User - PUT /users/{id}
// --------------------------------------------------
export const editUser = createAsyncThunk<
  UserType,
  { id: number; userData: UpdateUserPayload },
  { rejectValue: string }
>('users/editUser', async ({ id, userData }, { rejectWithValue }) => {
  try {
    const res = await axiosClient.put(`/users/${id}`, userData);
    return res.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// --------------------------------------------------
// Delete User - DELETE /users/{id}
// --------------------------------------------------
export const deleteUser = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('users/deleteUser', async (id, { rejectWithValue }) => {
  try {
    await axiosClient.delete(`/users/${id}`);
    return id;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// --------------------------------------------------
// Get User by ID - GET /users/{id}
// --------------------------------------------------
export const fetchUserById = createAsyncThunk<
  UserType,
  number,
  { rejectValue: string }
>('users/fetchUserById', async (userId, { rejectWithValue }) => {
  try {
    const res = await axiosClient.get(`/users/${userId}`);
    return res.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// --------------------------------------------------
// ADDED: Get Current User - GET /users/me
// --------------------------------------------------
export const fetchCurrentUser = createAsyncThunk<
  UserType,
  void,
  { rejectValue: string }
>('users/fetchCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const res = await axiosClient.get('/users/me');
    return res.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// --------------------------------------------------
// ADDED: Update Current User - PUT /users/me
// --------------------------------------------------
export const updateCurrentUser = createAsyncThunk<
  UserType,
  UpdateUserPayload,
  { rejectValue: string }
>('users/updateCurrentUser', async (userData, { rejectWithValue }) => {
  try {
    const res = await axiosClient.put('/users/me', userData);
    return res.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// --------------------------------------------------
// Slice
// --------------------------------------------------
const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    resetUsers: state => {
      state.data = [];
      state.loading = false;
      state.error = null;
      state.currentUser = null; // ← ADDED: Reset current user
    },
    // ADDED: Current user actions
    setCurrentUser: (state, action: PayloadAction<UserType>) => {
      state.currentUser = action.payload;
    },
    clearCurrentUser: state => {
      state.currentUser = null;
    }
  },
  extraReducers: builder => {
    builder
      // ---------------- Fetch Users ----------------
      .addCase(fetchUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUsers.fulfilled,
        (state, action: PayloadAction<UserType[]>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(fetchUsers.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch users';
        state.loading = false;
      })

      // ---------------- Add User ----------------
      .addCase(addUser.pending, state => {
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state, action: PayloadAction<UserType>) => {
        state.data.push(action.payload);
        state.error = null;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.error = action.payload || 'Failed to create user';
      })

      // ---------------- Edit User ----------------
      .addCase(editUser.pending, state => {
        state.error = null;
      })
      .addCase(editUser.fulfilled, (state, action: PayloadAction<UserType>) => {
        const index = state.data.findIndex(u => u.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(editUser.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update user';
      })

      // ---------------- Delete User ----------------
      .addCase(deleteUser.pending, state => {
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<number>) => {
        state.data = state.data.filter(u => u.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete user';
      })

      // ---------------- Fetch User by ID ----------------
      .addCase(
        fetchUserById.fulfilled,
        (state, action: PayloadAction<UserType>) => {
          const index = state.data.findIndex(u => u.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          } else {
            state.data.push(action.payload);
          }
        }
      )

      // ADDED: ---------------- Fetch Current User ----------------
      .addCase(fetchCurrentUser.pending, state => {
        state.loading = true;
      })
      .addCase(
        fetchCurrentUser.fulfilled,
        (state, action: PayloadAction<UserType>) => {
          state.currentUser = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.currentUser = null;
        state.error = action.payload || 'Failed to fetch current user';
        state.loading = false;
      })

      // ADDED: ---------------- Update Current User ----------------
      .addCase(
        updateCurrentUser.fulfilled,
        (state, action: PayloadAction<UserType>) => {
          state.currentUser = action.payload;

          // Also update in users list if present
          const index = state.data.findIndex(u => u.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
          state.error = null;
        }
      )
      .addCase(updateCurrentUser.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update user';
      });
  }
});

export const { clearError, resetUsers, setCurrentUser, clearCurrentUser } =
  userSlice.actions;

export default userSlice.reducer;
